// pages/pharmacies/PharmacyFinder.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Grid3X3, List, Map, SlidersHorizontal } from 'lucide-react';
import SearchBar from '../../components/common/SearchBar';
import PharmacyFilters from '../../components/common/PharmacyFilters';
import PharmacyList from '../../components/lists/PharmacyList';
import PharmacyMap from '../../components/maps/PharmacyMap';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import { pharmacyService } from '../../services/pharmacyService';

const PharmacyFinder = () => {
  // State management
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    deliveryService: false,
    open24Hours: false,
    radius: 25,
    minRating: '',
    maxDeliveryFee: '',
    hasParking: false,
    wheelchairAccessible: false,
    acceptsInsurance: false
  });
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('distance');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });
  const [error, setError] = useState('');
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Get user's current location on mount
  useEffect(() => {
    const initLocation = async () => {
      setLocationLoading(true);
      setLocationError('');
      
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
            setLocationLoading(false);
            setInitialLoadComplete(true);
          },
          (error) => {
            console.warn('Location access denied:', error);
            setLocationError('Location access denied. Showing all pharmacies.');
            setLocationLoading(false);
            setInitialLoadComplete(true);
          }
        );
      } else {
        setLocationError('Geolocation not supported');
        setLocationLoading(false);
        setInitialLoadComplete(true);
      }
    };

    initLocation();
  }, []);

  // Fetch pharmacies
  const fetchPharmacies = useCallback(async (page = 1) => {
    if (!initialLoadComplete) return;

    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        limit: pagination.itemsPerPage,
        isActive: 'true'
      };

      // Location-based search
      if (location) {
        params.latitude = location.latitude;
        params.longitude = location.longitude;
        params.radius = filters.radius;
      } else {
        params.sortBy = sortBy === 'distance' ? 'rating' : sortBy;
        params.sortOrder = sortBy === 'rating' ? 'desc' : 'asc';
      }

      // Search query
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch;
      }

      // Filters
      if (filters.deliveryService) params.deliveryService = 'true';
      if (filters.open24Hours) params.open24Hours = 'true';
      if (filters.minRating) params.minRating = filters.minRating;
      if (filters.maxDeliveryFee) params.maxDeliveryFee = filters.maxDeliveryFee;

      const response = await pharmacyService.getAllPharmacies(params);

      if (response.success) {
        const pharmaciesData = response.data.pharmacies || response.data || [];
        setPharmacies(pharmaciesData);
        
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.currentPage,
            totalPages: response.data.pagination.totalPages,
            totalItems: response.data.pagination.totalItems,
            itemsPerPage: response.data.pagination.itemsPerPage
          });
        } else {
          setPagination(prev => ({
            ...prev,
            currentPage: page,
            totalItems: pharmaciesData.length,
            totalPages: Math.ceil(pharmaciesData.length / prev.itemsPerPage)
          }));
        }
      } else {
        setError(response.message || 'Failed to load pharmacies');
        setPharmacies([]);
      }
    } catch (err) {
      console.error('Fetch pharmacies error:', err);
      setError('Failed to load pharmacies. Please try again.');
      setPharmacies([]);
    } finally {
      setLoading(false);
    }
  }, [
    location,
    debouncedSearch,
    filters.deliveryService,
    filters.open24Hours,
    filters.minRating,
    filters.maxDeliveryFee,
    filters.radius,
    sortBy,
    pagination.itemsPerPage,
    initialLoadComplete
  ]);

  useEffect(() => {
    if (initialLoadComplete) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      fetchPharmacies(1);
    }
  }, [
    debouncedSearch,
    filters.deliveryService,
    filters.open24Hours,
    filters.minRating,
    filters.maxDeliveryFee,
    filters.radius,
    sortBy,
    initialLoadComplete
  ]);

  // Request location handler for map
  const handleRequestLocation = useCallback(async () => {
    await getCurrentLocation();
  }, []);

  // Manually request location
  const getCurrentLocation = useCallback(async () => {
    setLocationLoading(true);
    setLocationError('');
    setError('');
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationLoading(false);
          setPagination(prev => ({ ...prev, currentPage: 1 }));
          fetchPharmacies(1);
        },
        (error) => {
          setLocationError('Unable to get your location');
          setError(`Unable to get your location: ${error.message}`);
          setLocationLoading(false);
        }
      );
    } else {
      setLocationError('Geolocation not supported');
      setError('Geolocation is not supported by your browser');
      setLocationLoading(false);
    }
  }, [fetchPharmacies]);

  // Event handlers
  const handleSearch = (query) => setSearchQuery(query);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      deliveryService: false,
      open24Hours: false,
      radius: 25,
      minRating: '',
      maxDeliveryFee: '',
      hasParking: false,
      wheelchairAccessible: false,
      acceptsInsurance: false
    });
    setSearchQuery('');
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchPharmacies(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePharmacyClick = (pharmacy) => {
    setSelectedPharmacy(pharmacy);
    window.location.href = `/pharmacies/${pharmacy._id}`;
  };

  const handleCallPharmacy = (phone) => {
    if (phone) window.location.href = `tel:${phone}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Find Pharmacies Near You</h1>
            <p className="text-lg text-primary-100 max-w-2xl mx-auto">
              Discover quality pharmacies with delivery services, medicine availability, and verified prescriptions.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-4xl mx-auto">
            <SearchBar
              value={searchQuery}
              onChange={handleSearch}
              onLocationRequest={getCurrentLocation}
              locationLoading={locationLoading}
              placeholder="Search pharmacies, medicines, or locations..."
              className="w-full"
            />
          </div>

          {/* Quick Stats */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{pagination.totalItems}+</div>
              <div className="text-primary-100 text-sm">Verified Pharmacies</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">24/7</div>
              <div className="text-primary-100 text-sm">Delivery Available</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">5000+</div>
              <div className="text-primary-100 text-sm">Medicines in Stock</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">Fast</div>
              <div className="text-primary-100 text-sm">Same-Day Delivery</div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            
            {/* Location Info */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              {location && (
                <span className="text-sm text-gray-600 bg-primary-50 px-3 py-1 rounded-full">
                  📍 Within {filters.radius}km of your location
                </span>
              )}
              
              {locationError && !location && (
                <span className="text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                  ⚠️ Location unavailable - showing all pharmacies
                </span>
              )}
              
              <span className="text-sm text-gray-600">
                {pagination.totalItems} pharmacies found
              </span>
            </div>

            {/* View Controls */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* Sort Dropdown */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              >
                {location && <option value="distance">Nearest First</option>}
                <option value="rating">Highest Rated</option>
                <option value="name">Name A-Z</option>
                <option value="createdAt">Recently Added</option>
              </select>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'grid' ? 'bg-white shadow text-primary-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Grid view"
                  aria-label="Grid view"
                >
                  <Grid3X3 size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'list' ? 'bg-white shadow text-primary-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="List view"
                  aria-label="List view"
                >
                  <List size={16} />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'map' ? 'bg-white shadow text-primary-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Map view"
                  aria-label="Map view"
                >
                  <Map size={16} />
                </button>
              </div>

              {/* Filters Toggle */}
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  showAdvancedFilters ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-gray-300 hover:bg-gray-50'
                }`}
                aria-label="Toggle filters"
              >
                <SlidersHorizontal size={16} />
                Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <PharmacyFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        showAdvancedFilters={showAdvancedFilters}
        onToggleAdvancedFilters={() => setShowAdvancedFilters(!showAdvancedFilters)}
        onClearFilters={clearFilters}
      />

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-700 whitespace-pre-line">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-500 hover:text-red-700 text-xl leading-none"
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {viewMode === 'map' ? (
          <PharmacyMap
            pharmacies={pharmacies}
            center={location || { latitude: -1.2921, longitude: 36.8219 }}
            userLocation={location}
            selectedPharmacy={selectedPharmacy}
            onPharmacySelect={setSelectedPharmacy}
            className="w-full h-[600px]"
            showRadius={true}
            radiusKm={filters.radius}
            showClusters={true}
            showControls={true}
            onRequestLocation={handleRequestLocation}
          />
        ) : loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner 
              size="lg" 
              variant="medical" 
              text={location ? "Finding pharmacies near you..." : "Loading pharmacies..."} 
            />
          </div>
        ) : pharmacies.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Map size={64} className="mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No pharmacies found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your search criteria or filters
            </p>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <>
            <PharmacyList
              pharmacies={pharmacies}
              loading={loading}
              viewMode={viewMode}
              showDistance={!!location}
              onPharmacyClick={handlePharmacyClick}
              onCallPharmacy={handleCallPharmacy}
            />

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={handlePageChange}
                  showInfo={true}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer Stats */}
      <div className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-primary-600 mb-2">{pagination.totalItems}+</div>
              <div className="text-gray-600">Verified Pharmacies</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
              <div className="text-gray-600">Delivery Available</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">5000+</div>
              <div className="text-gray-600">Medicines in Stock</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">Fast</div>
              <div className="text-gray-600">Same-Day Delivery</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PharmacyFinder;